import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SignupConfigDto } from "@delta/shared";
import { useLang } from "../i18n";
import { useSession } from "../lib/session";
import { api, DEMO_NOTE } from "../lib/api";
import { Icon } from "./Icon";

// Try a demo account (request of 30 Oct 2026, items 4 and 5; migration 055). While a demo business is signed in, the
// body carries the is-demo class (print previews show DEMO / NOT A TAX INVOICE) and every screen shows the banner:
// when the demo ends, Exit demo, and the registration link only while public signup is open. The server enforces
// the end, the locks and the allowance whatever this page shows.

/** Whether a demo business is signed in (for print previews, which have no session context of their own). */
export const demoMode = () => document.body.classList.contains("is-demo");

const note = (v: "ended" | "exited") => { try { sessionStorage.setItem(DEMO_NOTE, v); } catch { /* storage blocked */ } };

export function DemoBanner() {
  const { me, refresh } = useSession();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const ends = me?.demo?.expiresAt ? Date.parse(me.demo.expiresAt) : null;
  const [now, setNow] = useState(Date.now());
  const [signupOpen, setSignupOpen] = useState(false);
  useEffect(() => {
    if (!ends) return;
    document.body.classList.add("is-demo");
    return () => document.body.classList.remove("is-demo");
  }, [ends]);
  useEffect(() => {
    if (!ends) return;
    api<SignupConfigDto>("GET", "/signup/config").then((c) => setSignupOpen(c.mode !== "closed"), () => setSignupOpen(false));
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [ends]);
  // Time up: the server has closed the demo; asking again signs out and the sign-in page says why.
  useEffect(() => {
    if (ends && now >= ends) { note("ended"); void refresh(); }
  }, [ends, now, refresh]);
  if (!ends) return null;
  const left = Math.max(0, Math.floor((ends - now) / 1000));
  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
  const at = new Date(ends).toLocaleTimeString(lang === "ar" ? "ar-SA-u-nu-latn" : "en-GB", { hour: "2-digit", minute: "2-digit" });
  const exit = async () => {
    note("exited");
    await api("POST", "/demo/exit").catch(() => undefined);
    await refresh();
    nav("/login", { replace: true });
  };
  return (
    <div className="demo-banner" role="status" data-testid="demo-banner">
      <Icon name="alert" /><span>{t("demoBanner", { t: at, left: mmss })}</span>
      <span className="demo-banner-actions">
        {signupOpen && <a href="/signup" data-testid="demo-register" onClick={(e) => { e.preventDefault(); void exit().then(() => nav("/signup")); }}>{t("demoRegister")}</a>}
        <button className="btn btn-sm" data-testid="demo-exit" onClick={() => void exit()}>{t("demoExit")}</button>
      </span>
    </div>
  );
}

/** On screens whose changes are locked in a demo: says so above the screen (the server refuses them anyway). */
export function DemoLockNotice() {
  const { me } = useSession();
  const { t } = useLang();
  if (!me?.demo) return null;
  return <div className="dlg-alert show demo-lock" role="note" data-testid="demo-locked"><Icon name="lock" /><span>{t("demoLocked")}</span></div>;
}

/** DEMO / NOT A TAX INVOICE on a print preview of a demo's document. */
export function DemoPrintMark({ className }: { className: string }) {
  const { t } = useLang();
  if (!demoMode()) return null;
  return <div className={className} data-testid="print-demo">{t("demoNotTax")}</div>;
}

/** The sign-in page's "Try a demo account", shown only while the Super Admin has the demo on. */
export function DemoStart() {
  const { t } = useLang();
  const { refresh } = useSession();
  const nav = useNavigate();
  const [cfg, setCfg] = useState<{ enabled: boolean; minutes: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [said] = useState(() => { try { const v = sessionStorage.getItem(DEMO_NOTE); sessionStorage.removeItem(DEMO_NOTE); return v; } catch { return null; } });
  useEffect(() => { api<{ enabled: boolean; minutes: number }>("GET", "/demo/config").then(setCfg, () => setCfg(null)); }, []);
  // The website's "Try a demo account" opens this page with ?demo=1: the button is brought into view and focused, and
  // the visitor still starts the demo with their own click (nothing starts from a link).
  const btn = useRef<HTMLButtonElement>(null);
  const asked = useState(() => new URLSearchParams(location.search).get("demo") === "1")[0];
  useEffect(() => { if (asked && cfg?.enabled) { btn.current?.scrollIntoView({ block: "center" }); btn.current?.focus(); } }, [asked, cfg?.enabled]);
  const start = async () => {
    setBusy(true); setErr(null);
    try {
      await api("POST", "/demo/start");
      await refresh();
      nav("/home", { replace: true });
    } catch (e) {
      const code = (e as { code?: string }).code;
      setErr(t(code === "demo_full" ? "errDemoFull" : code === "demo_rate_limited" ? "errDemoRate" : code === "demo_real_session" ? "errDemoRealSession" : code === "demo_disabled" ? "errDemoDisabled" : "serverError"));
    } finally { setBusy(false); }
  };
  return <>
    {said && <div className="dlg-alert show" role="status" data-testid="demo-note"><Icon name="check" /><span>{t(said === "ended" ? "demoEnded" : "demoExited")}</span></div>}
    {cfg?.enabled && <div className="demo-start">
      <button ref={btn} type="button" className={`btn ${asked ? "btn-primary" : ""}`} data-testid="demo-start" disabled={busy} onClick={() => void start()}><Icon name="cart" />{busy ? t("demoStarting") : t("demoTry")}</button>
      <p className="login-alt">{t("demoTryHint", { m: cfg.minutes })}</p>
      {err && <div className="dlg-alert show" role="alert" data-testid="demo-error"><Icon name="alert" /><span>{err}</span></div>}
    </div>}
  </>;
}

